import classNames from 'classnames';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ReactComponent as ArrowListIcon } from 'assets/images/icons/arrow-list.svg';

import { VALIDATION_CLASS } from 'utils/constants';

import styles from 'assets/scss/ui/Select.module.scss';
import useDebounce from 'hooks/useDebounce';

interface SelectOption {
  value: string;
  label: string;
  searchLabel?: string;
  displayLabel?: string;
  flagClass?: string;
}

interface SelectProps {
  label?: string;
  error?: string;
  name: string;
  value?: string;
  placeholder?: string;
  options: SelectOption[];
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onSearchChange?: (query: string) => void;
  onScrollEnd?: () => void;
  wrapperClassName?: string;
  className?: string;
  disabled?: boolean;
  withSearch?: boolean;
  loading?: boolean;
  loadingMore?: boolean;
  dropdownClassName?: string;
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  name,
  value,
  placeholder = 'Select option',
  options,
  onChange,
  onBlur,
  onSearchChange,
  onScrollEnd,
  wrapperClassName,
  className,
  disabled = false,
  withSearch = false,
  loading = false,
  loadingMore = false,
  dropdownClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleDropdownScroll = useCallback(() => {
    const el = dropdownRef.current;
    if (!el || !onScrollEnd) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      onScrollEnd();
    }
  }, [onScrollEnd]);
  const selectedOption = useMemo(() => {
    if (!value) return undefined;

    return (options || [])?.find((option) => option.value === value);
  }, [options, value]);
  const [inputValue, setInputValue] = useState(selectedOption?.label || '');
  const debouncedValue = useDebounce(inputValue, 300);
  const prevValueRef = useRef(value);
  const syncedValueRef = useRef<string | undefined>(undefined);

  const handleSelectClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionClick = (optionValue: string) => {
    onChange?.(optionValue);
    setIsOpen(false);
  };

  const handleInputChange = (event: SyntheticEvent) => {
    if (disabled) return;
    setInputValue((event.target as HTMLInputElement).value);
    setIsOpen(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setInputValue(selectedOption?.label || '');
        setIsOpen(false);
        onBlur?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onBlur, selectedOption?.label]);

  useLayoutEffect(() => {
    if (onSearchChange && withSearch) {
      if (value == null || value === '') {
        if (
          prevValueRef.current != null &&
          prevValueRef.current !== '' &&
          (value == null || value === '')
        ) {
          setInputValue('');
          syncedValueRef.current = undefined;
        }
      } else if (selectedOption?.label) {
        const valueChanged = prevValueRef.current !== value;

        if (valueChanged) {
          setInputValue(selectedOption.label);
          syncedValueRef.current = value;
        } else if (syncedValueRef.current !== value && inputValue !== selectedOption.label) {
          setInputValue(selectedOption.label);
          syncedValueRef.current = value;
        }
      }
      prevValueRef.current = value;
      return;
    }

    if (selectedOption?.label) {
      setInputValue(selectedOption.label);
    } else if (
      prevValueRef.current != null &&
      prevValueRef.current !== '' &&
      (value == null || value === '')
    ) {
      setInputValue('');
    }
    prevValueRef.current = value;
  }, [selectedOption?.label, value, onSearchChange, withSearch, inputValue]);

  useEffect(() => {
    if (onSearchChange && debouncedValue !== selectedOption?.label) {
      onSearchChange(debouncedValue);
    }
  }, [debouncedValue, onSearchChange, selectedOption?.label]);

  return (
    <div
      title={selectedOption?.label}
      className={classNames(styles.selectWrapper, wrapperClassName, className, {
        [VALIDATION_CLASS]: !!error,
      })}
    >
      <div
        ref={selectRef}
        className={classNames(styles.selectContainer, {
          [styles.selectContainerError]: error,
          [styles.selectContainerDisabled]: disabled,
        })}
      >
        {label && (
          <label htmlFor={name} className={styles.label}>
            {label}
          </label>
        )}

        <div
          className={classNames(styles.select, {
            [styles.selectOpen]: isOpen,
          })}
          onClick={handleSelectClick}
        >
          <input
            type="text"
            value={inputValue}
            readOnly={!withSearch}
            placeholder={placeholder}
            onChange={handleInputChange}
            className={classNames(styles.selectValue, {
              [styles.selectPlaceholder]: !inputValue,
            })}
            disabled={disabled}
          />

          <ArrowListIcon
            className={classNames(styles.selectArrow, {
              [styles.selectArrowOpen]: isOpen,
            })}
          />
        </div>

        {isOpen && (
          <div
            ref={dropdownRef}
            className={classNames(styles.dropdown, dropdownClassName)}
            onScroll={onScrollEnd ? handleDropdownScroll : undefined}
          >
            {loading && (
              <div className={styles.option} style={{ justifyContent: 'center' }}>
                Loading...
              </div>
            )}

            {!loading &&
              options
                ?.filter((item) => {
                  if (onSearchChange) return true;
                  if (!withSearch || disabled) return true;
                  if (debouncedValue === selectedOption?.label) return true;

                  const searchText = (item.searchLabel || item.label) ?? '';
                  return searchText
                    .toLowerCase()
                    .trim()
                    .includes(debouncedValue?.trim()?.toLowerCase());
                })
                ?.map((option) => (
                  <div
                    key={option.value}
                    className={classNames(styles.option, {
                      [styles.optionSelected]:
                        value != null && value !== '' && option.value === value,
                    })}
                    onClick={() => handleOptionClick(option.value)}
                  >
                    {option.flagClass && (
                      <span className={classNames(styles.optionFlag, option.flagClass)} />
                    )}
                    {option.displayLabel || option.searchLabel || option.label}
                  </div>
                ))}

            {!loading && !loadingMore && options?.length === 0 && (
              <div className={styles.option} style={{ justifyContent: 'center' }}>
                {onSearchChange ? 'No results' : 'No options'}
              </div>
            )}

            {loadingMore && (
              <div className={styles.option} style={{ justifyContent: 'center' }}>
                Loading...
              </div>
            )}
          </div>
        )}
      </div>

      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
};
